import { SilverseaHelpers } from "../helpers";
import { ContentInteraction, LivepersonData, CruiseDrawerData } from "../interfaces";
import { Metric } from "web-vitals";
import { ConsentSettings } from "../consentSettings";
import CRC32 from "crc-32";

export interface ConversionEventDetail {
  request_type: string;
  wrid?: string;
  uwrid: string;
  brochure?: string;
  is_agent: boolean;
  formName: string;
  phone_number_is_present?: boolean;
  Email_hash?: string;
  Phone_hash?: string;
  NameFirst_hash?: string;
  NameLast_hash?: string;
  purchase?: {
    transaction_id: string;
    revenue: number;
    payment_type: string;
  };
}

export abstract class AnalyticsProvider extends SilverseaHelpers {
  protected _name: string = undefined;
  protected name_scheme =
    "background-color: black; color: white; padding: 0.5em";
  protected _has_started = false;
  has_consent_to_run?: boolean = false;
  initial_page_data_recieved?: boolean = false;

  set name(name: string) {
    this._name = name;
    this.name_scheme = this.get_name_scheme();
  }

  get name(): string {
    return this._name;
  }

  set has_started(value: boolean) {
    this._has_started = value;
    this.js_event("analytics-ready", "init-ready");
    this.log("has started...");

    wspr.S(
      "pageload",
      `analytics_provider_${this.name}`,
      async (wspr_event) => {
        this.log(`analytics_provider_${this.name} -> PAGELOAD TRIGGERED`);
        const event = wspr_event.m;
        this.pageload_triggered();
        return true;
      }
    );
  }

  get has_started(): boolean {
    return this._has_started;
  }

  start(_consent_settings: ConsentSettings): void {
    return;
  }

  get_name_scheme() {
    const pal = [
      "background-color: red; color: white; padding: 0.5em",
      "background-color: lime; color: black; padding: 0.5em",
      "background-color: blue; color: white; padding: 0.5em",
      "background-color: yellow; color: black; padding: 0.5em",
      "background-color: cyan; color: black; padding: 0.5em",
      "background-color: magenta; color: black; padding: 0.5em",
      "background-color: silver; color: black; padding: 0.5em",
      "background-color: gray; color: white; padding: 0.5em",
      "background-color: maroon; color: white; padding: 0.5em",
      "background-color: olive; color: white; padding: 0.5em",
      "background-color: green; color: white; padding: 0.5em",
      "background-color: purple; color: white; padding: 0.5em",
      "background-color: teal; color: white; padding: 0.5em",
      "background-color: navy; color: white; padding: 0.5em"
    ];
    return pal[Math.abs(CRC32.str(this.name)) % pal.length];
  }

  log(message): void {
    if (window.ss_show_logs) {
      console.log(
        "%c%s%cSilverseaVisitor->%cProvider->%c%s-> ",
        "background-color: darkorange; padding: 0.5em;",
        String.fromCharCode(187),
        "background-color: #323234; color: #fcfcfd; padding: 0.5em",
        "background-color: purple; color: white; padding: 0.5em",
        this.name_scheme,
        this.name,
        message
      );
    }
  }

  debug(message): void {
    if (window.ss_show_logs) {
      console.debug(
        "%c%s%cSilverseaVisitor->%cProvider->%c%s-> ",
        "background-color: darkorange; padding: 0.5em;",
        String.fromCharCode(187),
        "background-color: #323234; color: #fcfcfd; padding: 0.5em",
        "background-color: purple; color: white; padding: 0.5em",
        this.name_scheme,
        this.name,
        message
      );
    }
  }

  log_scroll(): void {
    this.log("SCROLL TRIGGERED");
  }

  scroll_triggered(_scroll_perc: number): void {
    return;
  }

  log_conversion(): void {
    this.log("CONVERSION TRIGGERED");
  }

  log_conversion_not_allowed(): void {
    this.log("NOT ALLOWED TO TRIGGER CONVERSION");
  }

  conversion_triggered(_conversion_event_detail: ConversionEventDetail): void {
    return;
  }

  log_interaction(): void {
    this.log("INTERACTION TRIGGERED");
  }

  interaction_triggered(_data_content_attribs: ContentInteraction): void {
    return;
  }

  log_pageload(): void {
    this.log("PAGELOAD TRIGGERED");
  }

  log_liveperson_load(): void {
    this.log("LIVEPERSON OPEN");
  }

  liveperson_load(_liveperson_data: LivepersonData): void {
    return;
  }

  cruise_drawer_opened(_cruise_drawer_data: CruiseDrawerData): void {
    return;
  }

  log_cruise_drawer_opened(): void {
    this.log("CRUIS DRAWER OPENED");
  }

  initialise_subscribers(): void {
    if (!this.initial_page_data_recieved) {
      // initialise the subscribers who may need the page data
      // console.log(`SUPER analytics_provider_${this.name}`);
      wspr.S(
        "web_request_submitted",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.log(`analytics_provider_${this.name} -> WEB REQUEST SUBMITTED`);
          const event = wspr_event.m;
          this.conversion_triggered(event);
          return true;
        }
      );
      wspr.S(
        "click_interaction",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.log(`analytics_provider_${this.name} -> CLICK`);
          const data_content_attribs = wspr_event.m;
          this.interaction_triggered(data_content_attribs);
          return true;
        }
      );
      wspr.S(
        "scroll",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.log(`analytics_provider_${this.name} -> SCROLL`);
          const data_content_attribs = wspr_event.m;
          this.scroll_triggered(data_content_attribs);
          return true;
        }
      );
      wspr.S(
        "liveperson_load",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.log(`analytics_provider_${this.name} -> LIVEPERSON LOAD`);
          const liveperson_data = wspr_event.m;
          this.liveperson_load(liveperson_data);
          return true;
        }
      );
      wspr.S(
        "cruise_drawer_opened_int",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.log(`analytics_provider_${this.name} -> CRUISE DRAWER OPENED`);
          const cruise_drawer_data = wspr_event.m;
          this.cruise_drawer_opened(cruise_drawer_data);
          return true;
        }
      );
    }
    this.initial_page_data_recieved = true;
    return;
  }

  pageload_triggered(): void {
    return;
  }
}
